import type { Store } from "@/data/repositories";
import type { Clock } from "@/domain/clock";
import { addDays, type DateKey } from "@/domain/dates";
import type { AnswerValue, Entry, MedicationIntake, MedicationSetting, Scenario, Subscription } from "@/domain/entities";
import { newId } from "@/domain/ids";
import { emptyAnswer, ScenarioRunner, type ValidationCode } from "@/domain/scenario/ScenarioRunner";

export class AnswerValidationError extends Error {
  constructor(readonly code: ValidationCode) {
    super(`Invalid answer: ${code}`);
    this.name = "AnswerValidationError";
  }
}

export class EntryService {
  constructor(
    private readonly store: Store,
    private readonly clock: Clock,
  ) {}

  async getOrCreate(subscription: Subscription, scenario: Scenario, date: DateKey): Promise<Entry> {
    const existing = await this.store.entries.byDate(subscription.id, date);
    if (existing) return existing;
    return this.create(subscription, scenario, date, {});
  }

  /** A further entry for the same day; only episodic scenarios allow it. The gate comes pre-answered. */
  async createEpisode(subscription: Subscription, scenario: Scenario, date: DateKey): Promise<Entry> {
    const gate = scenario.episodes;
    if (!gate) throw new Error(`Scenario ${scenario.slug} allows one entry per day`);
    return this.create(subscription, scenario, date, { [gate.gateQuestionId]: gate.gateValue });
  }

  private async create(subscription: Subscription, scenario: Scenario, date: DateKey, answers: Entry["answers"]): Promise<Entry> {
    const now = this.clock.now().toISOString();
    const runner = new ScenarioRunner(scenario);
    const entry: Entry = {
      id: newId(),
      subscriptionId: subscription.id,
      date,
      createdAt: now,
      timezone: this.clock.timeZone(),
      scenarioVersion: scenario.version,
      answers,
      status: runner.status(answers),
      updatedAt: now,
      deleted: false,
    };
    await this.store.entries.put(entry);
    return entry;
  }

  /** Validates and stores one answer. `undefined` skips an optional question. */
  async answer(entry: Entry, scenario: Scenario, questionId: string, value: AnswerValue | undefined): Promise<Entry> {
    const question = scenario.questions.find((q) => q.id === questionId);
    if (!question) throw new Error(`Unknown question: ${questionId}`);
    const runner = new ScenarioRunner(scenario);
    const error = runner.validate(question, value);
    if (error) throw new AnswerValidationError(error.code);
    const answers = { ...entry.answers, [questionId]: value ?? emptyAnswer(question) };
    const next: Entry = { ...entry, answers, status: runner.status(answers), updatedAt: this.clock.now().toISOString() };
    await this.store.entries.put(next);
    return next;
  }

  /** Throws away edits made since `snapshot` was taken: an untouched new entry is removed, otherwise the snapshot is written back. */
  async cancelEdits(snapshot: Entry): Promise<void> {
    if (snapshot.status === "partial" && snapshot.createdAt === snapshot.updatedAt) {
      await this.store.entries.remove(snapshot.id);
      return;
    }
    await this.store.entries.put({ ...snapshot, updatedAt: this.clock.now().toISOString() });
  }

  /** Removes an entry for good (a tombstone for sync); the caller confirms with the user first. */
  async remove(entry: Entry): Promise<void> {
    await this.store.entries.remove(entry.id);
  }

  async markSkipped(entry: Entry): Promise<Entry> {
    const next: Entry = { ...entry, status: "skipped", updatedAt: this.clock.now().toISOString() };
    await this.store.entries.put(next);
    return next;
  }

  /** Entries of the trailing `days` days ending today, oldest first. */
  /** Medicines typed into earlier entries of this subscription, most recent first; the last dose becomes the suggestion. */
  async medicineHistory(subscriptionId: string, scenario: Scenario): Promise<MedicationSetting[]> {
    const questionIds = scenario.questions.filter((q) => q.type === "medication").map((q) => q.id);
    if (questionIds.length === 0) return [];
    const entries = (await this.store.entries.listBySubscription(subscriptionId))
      .filter((e) => !e.deleted)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    const seen = new Map<string, MedicationSetting>();
    for (const entry of entries) {
      for (const id of questionIds) {
        for (const intake of intakesOf(entry.answers[id])) {
          const name = intake.name.trim();
          const key = name.toLowerCase();
          if (name && !seen.has(key)) seen.set(key, { name, defaultDose: intake.dose });
        }
      }
    }
    return [...seen.values()];
  }

  async listRecent(subscriptionId: string, today: DateKey, days: number): Promise<Entry[]> {
    return this.store.entries.listBySubscription(subscriptionId, { from: addDays(today, -(days - 1)), to: today });
  }
}

function intakesOf(value: AnswerValue | undefined): MedicationIntake[] {
  return Array.isArray(value) ? value.filter((item): item is MedicationIntake => typeof item === "object" && item !== null) : [];
}
